import React, {

    forwardRef,

    useEffect,

    useImperativeHandle,

    useMemo,

    useState

}

from "react";

import {

    OMNISCAN_BLUE

}

from "../../../../constants/colors";

import {

    functions

}

from "../../../../constants/common-functions";









//========================================================
// Image Composer Canvas
//
// Renders the selected source images using the current
// composition mode.
//
// SOURCE
//     Reconstructs the relative source-page geometry using
//     the persisted x / y / width / height values.
//
// HORIZONTAL
//     Places the selected images side by side.
//
// VERTICAL
//     Places the selected images one above another.
//
// GRID
//     Places the selected images into an automatic grid.
//
// Images are loaded through the normal authenticated API.
//
// The component also exposes renderComposition() through
// its ref. This creates a genuine PNG from the current
// composition without including any UI overlays.
//========================================================

const ImageComposerCanvas = forwardRef(

    function ImageComposerCanvas({

        images = [],

        layoutMode = "SOURCE"

    },

    ref)

    {

        //========================================================
        // Image URLs
        //========================================================

        const [

            imageUrls,

            setImageUrls

        ] = useState({});





        const [

            imageErrors,

            setImageErrors

        ] = useState({});





        const [

            loading,

            setLoading

        ] = useState(false);









        //========================================================
        // Load Images
        //========================================================

        useEffect(

            () =>

            {

                let cancelled =

                    false;


                const objectUrls =

                    [];


                //================================================
                // Reset
                //================================================

                setImageUrls(

                    {}

                );


                setImageErrors(

                    {}

                );


                if (

                    !Array.isArray(images) ||

                    images.length === 0

                )

                {

                    setLoading(

                        false

                    );


                    return () =>

                    {

                    };

                }


                setLoading(

                    true

                );


                let remaining =

                    images.length;


                //================================================
                // Complete One Image
                //================================================

                const completeImage = () =>

                {

                    remaining--;


                    if (

                        remaining <= 0 &&

                        !cancelled

                    )

                    {

                        setLoading(

                            false

                        );

                    }

                };


                //================================================
                // Load Each Image
                //================================================

                images.forEach(

                    (image, index) =>

                    {

                        const key =

                            getImageKey(

                                image,

                                index

                            );


                        const sectionId =

                            image?.documentSnippetSectionId;


                        //========================================
                        // No Source Section
                        //========================================

                        if (

                            sectionId == null

                        )

                        {

                            setImageErrors(

                                current =>

                                ({

                                    ...current,

                                    [key]:

                                        true

                                })

                            );


                            completeImage();


                            return;

                        }


                        //========================================
                        // API URL
                        //========================================

                        const url =

                            "/resolutionsoftwareplatform/snippet-sniffer/sections/" +

                            sectionId +

                            "/image";


                        //========================================
                        // Load Blob
                        //========================================

                        functions.getBlobFromAjax(

                            url,

                            function(blob)

                            {

                                if (

                                    cancelled

                                )

                                {

                                    return;

                                }


                                if (

                                    !blob

                                )

                                {

                                    setImageErrors(

                                        current =>

                                        ({

                                            ...current,

                                            [key]:

                                                true

                                        })

                                    );


                                    completeImage();


                                    return;

                                }


                                //================================
                                // Browser Object URL
                                //================================

                                const objectUrl =

                                    window.URL.createObjectURL(

                                        blob

                                    );


                                objectUrls.push(

                                    objectUrl

                                );


                                //================================
                                // Store URL
                                //================================

                                setImageUrls(

                                    current =>

                                    ({

                                        ...current,

                                        [key]:

                                            objectUrl

                                    })

                                );


                                completeImage();

                            }

                        );

                    }

                );


                //================================================
                // Cleanup
                //================================================

                return () =>

                {

                    cancelled =

                        true;


                    objectUrls.forEach(

                        objectUrl =>

                            window.URL.revokeObjectURL(

                                objectUrl

                            )

                    );

                };

            },

            [

                images

            ]

        );









        //========================================================
        // Source Geometry
        //========================================================

        const sourceGeometry =

            useMemo(

                () =>

                    calculateSourceGeometry(

                        images

                    ),

                [

                    images

                ]

            );









        //========================================================
        // Render Composition
        //
        // Produces a real PNG Blob from the current composition.
        //
        // UI elements such as image numbers are NOT rendered.
        //========================================================

        const renderComposition = async () =>

        {

            //====================================================
            // Validate
            //====================================================

            if (

                !Array.isArray(images) ||

                images.length === 0

            )

            {

                return null;

            }


            //====================================================
            // Make Sure Every Image Is Available
            //====================================================

            const loadedImages =

                await Promise.all(

                    images.map(

                        async (image, index) =>

                        {

                            const key =

                                getImageKey(

                                    image,

                                    index

                                );


                            const imageUrl =

                                imageUrls[key];


                            if (

                                !imageUrl

                            )

                            {

                                return null;

                            }


                            const htmlImage =

                                await loadHtmlImage(

                                    imageUrl

                                );


                            return {

                                source:

                                    image,

                                image:

                                    htmlImage,

                                index:

                                    index

                            };

                        }

                    )

                );


            //====================================================
            // Missing Image
            //====================================================

            if (

                loadedImages.some(

                    image =>
                        image == null

                )

            )

            {

                return null;

            }


            //====================================================
            // Render By Layout
            //====================================================

            if (

                layoutMode === "SOURCE"

            )

            {

                return renderSourceComposition(

                    loadedImages,

                    sourceGeometry

                );

            }


            if (

                layoutMode === "HORIZONTAL"

            )

            {

                return renderHorizontalComposition(

                    loadedImages

                );

            }


            if (

                layoutMode === "VERTICAL"

            )

            {

                return renderVerticalComposition(

                    loadedImages

                );

            }


            return renderGridComposition(

                loadedImages

            );

        };









        //========================================================
        // Public API
        //========================================================

        useImperativeHandle(

            ref,

            () =>

            ({

                renderComposition:

                    renderComposition

            }),

            [
                images,
                imageUrls,
                layoutMode,
                sourceGeometry
            ]

        );









        //========================================================
        // Empty
        //========================================================

        if (

            !images ||

            images.length === 0

        )

        {

            return (

                <div style={styles.empty}>

                    <div style={styles.emptyIcon}>

                        ▧

                    </div>


                    <div style={styles.emptyTitle}>

                        No images selected

                    </div>

                </div>

            );

        }









        //========================================================
        // Render
        //========================================================

        return (

            <div style={styles.root}>



                {/*============================================
                    Loading
                ============================================*/}

                {

                    loading &&

                    (

                        <div style={styles.loading}>

                            Loading source images...

                        </div>

                    )

                }





                {/*============================================
                    Composition
                ============================================*/}

                <div style={styles.composition}>

                    {

                        layoutMode === "SOURCE"

                            ?

                            (

                                <SourceLayout

                                    images={

                                        images

                                    }

                                    imageUrls={

                                        imageUrls

                                    }

                                    imageErrors={

                                        imageErrors

                                    }

                                    geometry={

                                        sourceGeometry

                                    }

                                />

                            )

                            :

                            layoutMode === "HORIZONTAL"

                                ?

                                (

                                    <HorizontalLayout

                                        images={

                                            images

                                        }

                                        imageUrls={

                                            imageUrls

                                        }

                                        imageErrors={

                                            imageErrors

                                        }

                                    />

                                )

                                :

                                layoutMode === "VERTICAL"

                                    ?

                                    (

                                        <VerticalLayout

                                            images={

                                                images

                                            }

                                            imageUrls={

                                                imageUrls

                                            }

                                            imageErrors={

                                                imageErrors

                                            }

                                        />

                                    )

                                    :

                                    (

                                        <GridLayout

                                            images={

                                                images

                                            }

                                            imageUrls={

                                                imageUrls

                                            }

                                            imageErrors={

                                                imageErrors

                                            }

                                        />

                                    )

                    }

                </div>

            </div>

        );

    }

);









//========================================================
// Source Layout
//========================================================

function SourceLayout({

    images,

    imageUrls,

    imageErrors,

    geometry

})

{

    //========================================================
    // No Usable Geometry
    //========================================================

    if (

        !geometry.valid

    )

    {

        return (

            <div style={styles.sourceFallback}>

                <div style={styles.sourceFallbackTitle}>

                    Source geometry unavailable

                </div>


                <div style={styles.sourceFallbackText}>

                    Showing images horizontally instead.

                </div>


                <HorizontalLayout

                    images={

                        images

                    }

                    imageUrls={

                        imageUrls

                    }

                    imageErrors={

                        imageErrors

                    }

                />

            </div>

        );

    }









    //========================================================
    // Render
    //========================================================

    return (

        <div style={styles.sourceStage}>

            <div

                style={

                    {

                        ...styles.sourceCanvas,

                        aspectRatio:

                            geometry.aspectRatio

                    }

                }

            >

                {

                    images.map(

                        (image, index) =>

                        {

                            const key =

                                getImageKey(

                                    image,

                                    index

                                );


                            const itemGeometry =

                                geometry.items[index];


                            if (

                                !itemGeometry

                            )

                            {

                                return null;

                            }


                            return (

                                <div

                                    key={

                                        key

                                    }

                                    style={

                                        {

                                            ...styles.sourceImageContainer,

                                            left:

                                                itemGeometry.left +
                                                "%",

                                            top:

                                                itemGeometry.top +
                                                "%",

                                            width:

                                                itemGeometry.width +
                                                "%",

                                            height:

                                                itemGeometry.height +
                                                "%"

                                        }

                                    }

                                >

                                    <ComposerImage

                                        image={

                                            image

                                        }

                                        imageUrl={

                                            imageUrls[key]

                                        }

                                        error={

                                            imageErrors[key]

                                        }

                                        index={

                                            index

                                        }

                                        fill={true}

                                    />

                                </div>

                            );

                        }

                    )

                }

            </div>

        </div>

    );

}









//========================================================
// Horizontal Layout
//========================================================

function HorizontalLayout({

    images,

    imageUrls,

    imageErrors

})

{

    return (

        <div style={styles.horizontalLayout}>

            {

                images.map(

                    (image, index) =>

                    {

                        const key =

                            getImageKey(

                                image,

                                index

                            );


                        return (

                            <div

                                key={

                                    key

                                }

                                style={styles.horizontalItem}

                            >

                                <ComposerImage

                                    image={

                                        image

                                    }

                                    imageUrl={

                                        imageUrls[key]

                                    }

                                    error={

                                        imageErrors[key]

                                    }

                                    index={

                                        index

                                    }

                                />

                            </div>

                        );

                    }

                )

            }

        </div>

    );

}









//========================================================
// Vertical Layout
//========================================================

function VerticalLayout({

    images,

    imageUrls,

    imageErrors

})

{

    return (

        <div style={styles.verticalLayout}>

            {

                images.map(

                    (image, index) =>

                    {

                        const key =

                            getImageKey(

                                image,

                                index

                            );


                        return (

                            <div

                                key={

                                    key

                                }

                                style={styles.verticalItem}

                            >

                                <ComposerImage

                                    image={

                                        image

                                    }

                                    imageUrl={

                                        imageUrls[key]

                                    }

                                    error={

                                        imageErrors[key]

                                    }

                                    index={

                                        index

                                    }

                                />

                            </div>

                        );

                    }

                )

            }

        </div>

    );

}









//========================================================
// Grid Layout
//========================================================

function GridLayout({

    images,

    imageUrls,

    imageErrors

})

{

    //========================================================
    // Columns
    //========================================================

    const columns =

        images.length <= 2

            ?

            2

            :

            Math.ceil(

                Math.sqrt(

                    images.length

                )

            );









    //========================================================
    // Render
    //========================================================

    return (

        <div

            style={

                {

                    ...styles.gridLayout,

                    gridTemplateColumns:

                        "repeat(" +
                        columns +
                        ", minmax(0, 1fr))"

                }

            }

        >

            {

                images.map(

                    (image, index) =>

                    {

                        const key =

                            getImageKey(

                                image,

                                index

                            );


                        return (

                            <div

                                key={

                                    key

                                }

                                style={styles.gridItem}

                            >

                                <ComposerImage

                                    image={

                                        image

                                    }

                                    imageUrl={

                                        imageUrls[key]

                                    }

                                    error={

                                        imageErrors[key]

                                    }

                                    index={

                                        index

                                    }

                                />

                            </div>

                        );

                    }

                )

            }

        </div>

    );

}









//========================================================
// Composer Image
//========================================================

function ComposerImage({

    image,

    imageUrl,

    error,

    index,

    fill = false

})

{

    //========================================================
    // Error
    //========================================================

    if (

        error

    )

    {

        return (

            <div style={styles.imageError}>

                <div style={styles.imageErrorIcon}>

                    !

                </div>


                <div>

                    Unable to load image

                </div>

            </div>

        );

    }









    //========================================================
    // Loading
    //========================================================

    if (

        !imageUrl

    )

    {

        return (

            <div style={styles.imageLoading}>

                Loading...

            </div>

        );

    }









    //========================================================
    // Render
    //========================================================

    return (

        <div

            style={

                {

                    ...styles.composerImage,

                    ...(fill

                        ?

                        styles.composerImageFill

                        :

                        {})

                }

            }

        >

            <img

                src={

                    imageUrl

                }

                alt={

                    image?.documentTitle ||
                    "Source Image"

                }

                style={

                    fill

                        ?

                        styles.imageFill

                        :

                        styles.imageContain

                }

                draggable={false}

            />


            <div style={styles.imageNumber}>

                {

                    String(

                        index + 1

                    ).padStart(

                        2,

                        "0"

                    )

                }

            </div>

        </div>

    );

}









//========================================================
// Render Source Composition
//========================================================

async function renderSourceComposition(

    loadedImages,

    geometry

)

{

    //========================================================
    // Geometry Fallback
    //========================================================

    if (

        !geometry?.valid

    )

    {

        return renderHorizontalComposition(

            loadedImages

        );

    }


    //========================================================
    // Output Resolution
    //
    // We preserve the source geometry ratio but scale the
    // composition to a useful PNG resolution.
    //========================================================

    const scale =

        calculateRenderScale(

            geometry.totalWidth,

            geometry.totalHeight,

            1800

        );


    const canvasWidth =

        Math.max(

            1,

            Math.round(

                geometry.totalWidth *
                scale

            )

        );


    const canvasHeight =

        Math.max(

            1,

            Math.round(

                geometry.totalHeight *
                scale

            )

        );


    //========================================================
    // Canvas
    //========================================================

    const canvas =

        document.createElement(

            "canvas"

        );


    canvas.width =

        canvasWidth;


    canvas.height =

        canvasHeight;


    const context =

        canvas.getContext(

            "2d"

        );


    //========================================================
    // White Background
    //========================================================

    context.fillStyle =

        "#ffffff";


    context.fillRect(

        0,

        0,

        canvasWidth,

        canvasHeight

    );


    //========================================================
    // Draw Images
    //========================================================

    loadedImages.forEach(

        (loadedImage, index) =>

        {

            const source =

                loadedImage.source;


            const x =

                (
                    Number(source.x) -
                    geometry.minX
                )
                *
                scale;


            const y =

                (
                    Number(source.y) -
                    geometry.minY
                )
                *
                scale;


            const width =

                Number(source.width) *
                scale;


            const height =

                Number(source.height) *
                scale;


            context.drawImage(

                loadedImage.image,

                x,

                y,

                width,

                height

            );

        }

    );


    //========================================================
    // PNG
    //========================================================

    return canvasToBlob(

        canvas

    );

}









//========================================================
// Render Horizontal Composition
//========================================================

async function renderHorizontalComposition(

    loadedImages

)

{

    const gap =

        12;


    const itemWidth =

        600;


    const itemHeight =

        500;


    const canvasWidth =

        (
            loadedImages.length *
            itemWidth
        )
        +
        (
            Math.max(
                0,
                loadedImages.length - 1
            )
            *
            gap
        );


    const canvasHeight =

        itemHeight;


    const canvas =

        createWhiteCanvas(

            canvasWidth,

            canvasHeight

        );


    const context =

        canvas.getContext(

            "2d"

        );


    loadedImages.forEach(

        (loadedImage, index) =>

        {

            drawContainedImage(

                context,

                loadedImage.image,

                index *
                (
                    itemWidth +
                    gap
                ),

                0,

                itemWidth,

                itemHeight

            );

        }

    );


    return canvasToBlob(

        canvas

    );

}









//========================================================
// Render Vertical Composition
//========================================================

async function renderVerticalComposition(

    loadedImages

)

{

    const gap =

        12;


    const itemWidth =

        900;


    const itemHeight =

        500;


    const canvasWidth =

        itemWidth;


    const canvasHeight =

        (
            loadedImages.length *
            itemHeight
        )
        +
        (
            Math.max(
                0,
                loadedImages.length - 1
            )
            *
            gap
        );


    const canvas =

        createWhiteCanvas(

            canvasWidth,

            canvasHeight

        );


    const context =

        canvas.getContext(

            "2d"

        );


    loadedImages.forEach(

        (loadedImage, index) =>

        {

            drawContainedImage(

                context,

                loadedImage.image,

                0,

                index *
                (
                    itemHeight +
                    gap
                ),

                itemWidth,

                itemHeight

            );

        }

    );


    return canvasToBlob(

        canvas

    );

}









//========================================================
// Render Grid Composition
//========================================================

async function renderGridComposition(

    loadedImages

)

{

    const columns =

        loadedImages.length <= 2

            ?

            2

            :

            Math.ceil(

                Math.sqrt(

                    loadedImages.length

                )

            );


    const rows =

        Math.ceil(

            loadedImages.length /
            columns

        );


    const gap =

        12;


    const itemWidth =

        500;


    const itemHeight =

        400;


    const canvasWidth =

        (
            columns *
            itemWidth
        )
        +
        (
            Math.max(
                0,
                columns - 1
            )
            *
            gap
        );


    const canvasHeight =

        (
            rows *
            itemHeight
        )
        +
        (
            Math.max(
                0,
                rows - 1
            )
            *
            gap
        );


    const canvas =

        createWhiteCanvas(

            canvasWidth,

            canvasHeight

        );


    const context =

        canvas.getContext(

            "2d"

        );


    loadedImages.forEach(

        (loadedImage, index) =>

        {

            const column =

                index %
                columns;


            const row =

                Math.floor(

                    index /
                    columns

                );


            const x =

                column *
                (
                    itemWidth +
                    gap
                );


            const y =

                row *
                (
                    itemHeight +
                    gap
                );


            drawContainedImage(

                context,

                loadedImage.image,

                x,

                y,

                itemWidth,

                itemHeight

            );

        }

    );


    return canvasToBlob(

        canvas

    );

}









//========================================================
// Draw Contained Image
//
// Equivalent to object-fit: contain.
//========================================================

function drawContainedImage(

    context,

    image,

    x,

    y,

    width,

    height

)

{

    const imageRatio =

        image.naturalWidth /
        image.naturalHeight;


    const containerRatio =

        width /
        height;


    let drawWidth;

    let drawHeight;

    let drawX;

    let drawY;


    if (

        imageRatio >
        containerRatio

    )

    {

        drawWidth =

            width;


        drawHeight =

            width /
            imageRatio;


        drawX =

            x;


        drawY =

            y +
            (
                (
                    height -
                    drawHeight
                )
                /
                2
            );

    }

    else

    {

        drawHeight =

            height;


        drawWidth =

            height *
            imageRatio;


        drawX =

            x +
            (
                (
                    width -
                    drawWidth
                )
                /
                2
            );


        drawY =

            y;

    }


    context.drawImage(

        image,

        drawX,

        drawY,

        drawWidth,

        drawHeight

    );

}









//========================================================
// Create White Canvas
//========================================================

function createWhiteCanvas(

    width,

    height

)

{

    const canvas =

        document.createElement(

            "canvas"

        );


    canvas.width =

        Math.max(

            1,

            Math.round(
                width
            )

        );


    canvas.height =

        Math.max(

            1,

            Math.round(
                height
            )

        );


    const context =

        canvas.getContext(

            "2d"

        );


    context.fillStyle =

        "#ffffff";


    context.fillRect(

        0,

        0,

        canvas.width,

        canvas.height

    );


    return canvas;

}









//========================================================
// Calculate Render Scale
//========================================================

function calculateRenderScale(

    width,

    height,

    maximumDimension

)

{

    const largestDimension =

        Math.max(

            width,

            height

        );


    if (

        largestDimension <= 0

    )

    {

        return 1;

    }


    return (

        maximumDimension /
        largestDimension

    );

}









//========================================================
// Load HTML Image
//========================================================

function loadHtmlImage(

    url

)

{

    return new Promise(

        (resolve, reject) =>

        {

            const image =

                new Image();


            image.onload = () =>

            {

                resolve(

                    image

                );

            };


            image.onerror = () =>

            {

                reject(

                    new Error(

                        "Unable to load source image."

                    )

                );

            };


            image.src =

                url;

        }

    );

}









//========================================================
// Canvas To Blob
//========================================================

function canvasToBlob(

    canvas

)

{

    return new Promise(

        resolve =>

        {

            canvas.toBlob(

                blob =>

                {

                    resolve(

                        blob

                    );

                },

                "image/png",

                1.0

            );

        }

    );

}









//========================================================
// Calculate Source Geometry
//========================================================

function calculateSourceGeometry(

    images

)

{

    if (

        !Array.isArray(images) ||

        images.length === 0

    )

    {

        return {

            valid:

                false,

            items:

                []

        };

    }


    const geometryItems =

        images.map(

            image =>

            {

                const x =

                    Number(
                        image?.x
                    );


                const y =

                    Number(
                        image?.y
                    );


                const width =

                    Number(
                        image?.width
                    );


                const height =

                    Number(
                        image?.height
                    );


                return {

                    x,

                    y,

                    width,

                    height,

                    valid:

                        Number.isFinite(x) &&

                        Number.isFinite(y) &&

                        Number.isFinite(width) &&

                        Number.isFinite(height) &&

                        width > 0 &&

                        height > 0

                };

            }

        );


    if (

        geometryItems.some(

            item =>
                !item.valid

        )

    )

    {

        return {

            valid:

                false,

            items:

                []

        };

    }


    const minX =

        Math.min(

            ...geometryItems.map(

                item =>
                    item.x

            )

        );


    const minY =

        Math.min(

            ...geometryItems.map(

                item =>
                    item.y

            )

        );


    const maxX =

        Math.max(

            ...geometryItems.map(

                item =>
                    item.x +
                    item.width

            )

        );


    const maxY =

        Math.max(

            ...geometryItems.map(

                item =>
                    item.y +
                    item.height

            )

        );


    const totalWidth =

        maxX -
        minX;


    const totalHeight =

        maxY -
        minY;


    if (

        totalWidth <= 0 ||

        totalHeight <= 0

    )

    {

        return {

            valid:

                false,

            items:

                []

        };

    }


    const items =

        geometryItems.map(

            item =>

            ({

                left:

                    (
                        (
                            item.x -
                            minX
                        )
                        /
                        totalWidth
                    )
                    *
                    100,

                top:

                    (
                        (
                            item.y -
                            minY
                        )
                        /
                        totalHeight
                    )
                    *
                    100,

                width:

                    (
                        item.width
                        /
                        totalWidth
                    )
                    *
                    100,

                height:

                    (
                        item.height
                        /
                        totalHeight
                    )
                    *
                    100

            })

        );


    const aspectRatio =

        totalWidth +
        " / " +
        totalHeight;


    return {

        valid:

            true,

        minX,

        minY,

        maxX,

        maxY,

        totalWidth,

        totalHeight,

        aspectRatio,

        items

    };

}









//========================================================
// Image Key
//========================================================

function getImageKey(

    image,

    index

)

{

    return (

        image?.basketId ||

        image?.documentSnippetSectionId ||

        "image-" +
        index

    );

}









//========================================================
// Styles
//========================================================

const styles =

{

    root:

    {

        position:

            "relative",

        width:

            "100%",

        height:

            "100%",

        minHeight:

            380,

        display:

            "flex",

        alignItems:

            "stretch",

        justifyContent:

            "stretch",

        boxSizing:

            "border-box"

    },





    //========================================================
    // Loading
    //========================================================

    loading:

    {

        position:

            "absolute",

        top:

            10,

        right:

            10,

        padding:

            "5px 8px",

        border:

            "1px solid rgba(32,175,232,0.18)",

        borderRadius:

            4,

        background:

            "rgba(255,255,255,0.92)",

        color:

            OMNISCAN_BLUE,

        fontSize:

            8,

        fontWeight:

            600,

        zIndex:

            20

    },





    //========================================================
    // Composition
    //========================================================

    composition:

    {

        flex:

            1,

        minWidth:

            0,

        minHeight:

            0,

        padding:

            22,

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        boxSizing:

            "border-box",

        overflow:

            "auto"

    },





    //========================================================
    // Source Layout
    //========================================================

    sourceStage:

    {

        width:

            "100%",

        height:

            "100%",

        minHeight:

            320,

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        boxSizing:

            "border-box"

    },

    sourceCanvas:

    {

        position:

            "relative",

        width:

            "100%",

        maxWidth:

            760,

        maxHeight:

            "100%",

        background:

            "#f5f5f5",

        border:

            "1px solid #d7dfe4",

        boxShadow:

            "0 8px 25px rgba(30,45,55,0.10)",

        overflow:

            "hidden"

    },

    sourceImageContainer:

    {

        position:

            "absolute",

        boxSizing:

            "border-box",

        overflow:

            "hidden"

    },

    sourceFallback:

    {

        width:

            "100%",

        textAlign:

            "center"

    },

    sourceFallbackTitle:

    {

        marginBottom:

            4,

        color:

            "#52636f",

        fontSize:

            11,

        fontWeight:

            600

    },

    sourceFallbackText:

    {

        marginBottom:

            18,

        color:

            "#9aa6ae",

        fontSize:

            9

    },





    //========================================================
    // Horizontal
    //========================================================

    horizontalLayout:

    {

        width:

            "100%",

        display:

            "flex",

        alignItems:

            "stretch",

        justifyContent:

            "center",

        gap:

            10

    },

    horizontalItem:

    {

        flex:

            "1 1 0",

        minWidth:

            0,

        height:

            360

    },





    //========================================================
    // Vertical
    //========================================================

    verticalLayout:

    {

        width:

            "100%",

        maxWidth:

            700,

        display:

            "flex",

        flexDirection:

            "column",

        gap:

            10

    },

    verticalItem:

    {

        width:

            "100%",

        height:

            260

    },





    //========================================================
    // Grid
    //========================================================

    gridLayout:

    {

        width:

            "100%",

        display:

            "grid",

        gap:

            10

    },

    gridItem:

    {

        minWidth:

            0,

        height:

            260

    },





    //========================================================
    // Composer Image
    //========================================================

    composerImage:

    {

        position:

            "relative",

        width:

            "100%",

        height:

            "100%",

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        boxSizing:

            "border-box",

        border:

            "1px solid #dce3e7",

        background:

            "#f8fafb",

        overflow:

            "hidden"

    },

    composerImageFill:

    {

        border:

            "none",

        background:

            "transparent"

    },

    imageContain:

    {

        display:

            "block",

        width:

            "100%",

        height:

            "100%",

        objectFit:

            "contain"

    },

    imageFill:

    {

        display:

            "block",

        width:

            "100%",

        height:

            "100%",

        objectFit:

            "fill"

    },

    imageNumber:

    {

        position:

            "absolute",

        top:

            6,

        left:

            6,

        minWidth:

            22,

        height:

            20,

        padding:

            "0 5px",

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        boxSizing:

            "border-box",

        borderRadius:

            4,

        background:

            "rgba(9,24,34,0.78)",

        color:

            "#ffffff",

        fontSize:

            8,

        fontWeight:

            700,

        pointerEvents:

            "none"

    },





    //========================================================
    // Status
    //========================================================

    imageLoading:

    {

        width:

            "100%",

        height:

            "100%",

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        color:

            "#9ba7af",

        fontSize:

            9

    },

    imageError:

    {

        width:

            "100%",

        height:

            "100%",

        display:

            "flex",

        flexDirection:

            "column",

        alignItems:

            "center",

        justifyContent:

            "center",

        gap:

            7,

        color:

            "#9ba7af",

        fontSize:

            9

    },

    imageErrorIcon:

    {

        width:

            28,

        height:

            28,

        display:

            "flex",

        alignItems:

            "center",

        justifyContent:

            "center",

        border:

            "1px solid #d8e0e5",

        borderRadius:

            4,

        color:

            "#8a98a2",

        fontSize:

            12,

        fontWeight:

            700

    },





    //========================================================
    // Empty
    //========================================================

    empty:

    {

        width:

            "100%",

        minHeight:

            320,

        display:

            "flex",

        flexDirection:

            "column",

        alignItems:

            "center",

        justifyContent:

            "center",

        color:

            "#87959f"

    },

    emptyIcon:

    {

        marginBottom:

            10,

        color:

            OMNISCAN_BLUE,

        fontSize:

            24

    },

    emptyTitle:

    {

        fontSize:

            11,

        fontWeight:

            600

    }

};









//========================================================
// Export
//========================================================

export default ImageComposerCanvas;